/**
 * REMOVE ADS: data model stub. No billing code lives here and nothing reads it
 * yet; see REMOVE-ADS-DESIGN.md in the launch folder.
 *
 * The flag is a cache, not an authority. Google Play's ownership query is the
 * truth once billing exists. It lives under its own key so that "Reset all
 * progress" and cloud save merges can never delete or forge a purchase.
 *
 * What Google Play Billing will need (none of it is done here):
 *  - a one time in app product id, for example `remove_ads`, made in Play Console by the owner
 *  - a payments profile and tax details on the owner's account
 *  - the com.android.vending.BILLING permission in AndroidManifest.xml
 *  - a Capacitor billing plugin or a thin custom plugin (Capacitor 6 has no first party one)
 *  - acknowledge every purchased state, a Restore purchase row, re-query on every launch
 *  - a billing enabled build uploaded before the product can be tested with license testers
 *  - data safety, content rating and privacy page updated in the same release
 */

export const ENTITLEMENT_KEY = 'nodus.entitlement.v1';

/** The Settings row and the buy flow stay hidden until billing exists. */
export const REMOVE_ADS_UI_ENABLED = false;

export interface Entitlement {
  removeAds: boolean;
  /** Epoch ms of the last check against Play. 0 = never checked. */
  checkedAt: number;
}

const NONE: Entitlement = { removeAds: false, checkedAt: 0 };

export function readEntitlement(): Entitlement {
  try {
    const raw = localStorage.getItem(ENTITLEMENT_KEY);
    if (!raw) return { ...NONE };
    const p = JSON.parse(raw) as Partial<Entitlement>;
    return { removeAds: p.removeAds === true, checkedAt: typeof p.checkedAt === 'number' ? p.checkedAt : 0 };
  } catch {
    return { ...NONE };
  }
}

export function hasRemoveAds(): boolean {
  return readEntitlement().removeAds;
}

/** Called only by the future billing module after Play confirms the state. */
export function setRemoveAds(owned: boolean, now = Date.now()): void {
  try {
    localStorage.setItem(ENTITLEMENT_KEY, JSON.stringify({ removeAds: owned, checkedAt: now } satisfies Entitlement));
  } catch { /* storage unavailable: treat as not owned */ }
}
